import cn from "classnames";

// Mui
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Button,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Table,
    Select,
    MenuItem,
    ListItemText,
    Chip,
    FormLabel,
    Skeleton,
} from "@mui/material";

// Images
import drax_lighthouse_animation from "assets/animations/drax_lighthouse_animation.gif";

// Icons
import icons from "enums/icons";

// Hooks
import { useIntl } from "react-intl";
import useUserManagement from "./useUserManagement";

// Styles
import UserManagementStyles from "./UserManagement.styles";

// Components
import CustomIcon from "components/CustomIcon";
import { Checkbox } from "components/Checkbox";
import { SortMenu } from "components/SortMenu";
import { ClearFilterButton } from "components/Buttons";
import { UserRoleSelect } from "pages/ClientHub/components";
import { HideColumnsMenu } from "components/HideColumnsMenu";
import NavButton from "components/HeaderNavigation/NavButton";
import { Header, Body } from "components/Layout/LayoutContent";
import TablePagination from "components/Talent/TablePagination";
import { ArchivedUsers, ArchiveUserConfirmation, SelectTeam, UserTableRow } from "./components";
import Modal from "components/Modal";
import { InviteUsersModal } from "pages/HubManagement/components/InviteUsers";

// Ability
import { AbilityContext } from "context/Ability";
import { useAbility } from "@casl/react";
import { isEmpty } from "lodash";

const UserManagement = () => {
    const intl = useIntl();
    const classes = UserManagementStyles();

    const ability = useAbility(AbilityContext);
    const canInviteUsers = ability.can("see", "SYS_ADMIN_INVITE_USERS");

    const {
        users,
        archivedUsers,
        teamsData,
        userRoles,
        filters,
        inputName,
        usersSort,
        paginationTextInputRef,
        tableHeader,
        hidableColumns,
        visibleColumns,
        adminsCount,
        disableAll,
        disableClearAll,
        shouldOpenArchiveModal,
        currentPage,
        shouldOpenTeamsModal,
        isAnyUserSelected,
        selectedUserName,
        activeTab,
        inviteModalOpen,
        pendingInvitations,
        orgId,
        handleSort,
        handleFilter,
        handleClearFilters,
        handleArchiveUser,
        handleSelectRole,
        toggleCheckUser,
        toggleCheckAllUsers,
        handleToggleColumn,
        handleOpenArchiveModal,
        handleCloseArchiveModal,
        handleUnarchiveUser,
        handlePageChange,
        handleOpenTeamsModal,
        handleCloseTeamsModal,
        handleConfirmAddTeam,
        handleChipSelect,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        setSelectedUserForTeam,
        handleInviteSelectedUsers,
        handleOpenInvitationModal,
        closeInviteModal,
        handleRemoveTeamFromUser,
    } = useUserManagement();

    return (
        <>
            <Header>
                <Box className={classes.headerInner}>
                    <Typography className={classes.headerTitle}>User Management</Typography>
                </Box>
            </Header>

            <Body>
                <Box className={classes.bodyHeader}>
                    <Typography className={classes.intro}>
                        Here you can manage who is sharing information with your organisation and what they can do. You
                        can also invite new members to join as well as add people to teams so that they can be viewed
                        together.
                    </Typography>
                    <Box className={classes.toolbox}>
                        <Box className={classes.filtersWrapper}>
                            <Box sx={{ maxWidth: 300, mr: 1 }}>
                                <TextField
                                    size="small"
                                    id="user-filter"
                                    name="user-filter"
                                    autoComplete="off"
                                    value={inputName}
                                    className={classes.filterByNameInput}
                                    onChange={handleFilter}
                                    placeholder={intl.formatMessage({
                                        id: "filters.placeholder.user",
                                    })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CustomIcon icon={icons.searchInverted} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            <Box sx={{ maxWidth: 200, mr: 1 }}>
                                <FormLabel className={classes.label}>
                                    {intl.formatMessage({ id: "filters.labels.role" })}
                                </FormLabel>
                                <Select
                                    multiple
                                    id="filter_roles"
                                    name="filter_roles"
                                    labelId="filter-by-role"
                                    value={filters.filter_roles}
                                    className={classes.selectField}
                                    onChange={handleFilter}
                                    IconComponent={(props) => (
                                        <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                                    )}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                    renderValue={(selected) => {
                                        const numSelected = selected.length - 1;
                                        const lastSelected = userRoles.find((role) => role.id === selected.at(-1));
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography
                                                    className={cn({
                                                        [classes.filterPlaceholderLabel]:
                                                            lastSelected?.id === "placeholder",
                                                    })}
                                                >
                                                    {lastSelected?.label}
                                                </Typography>
                                                {numSelected > 0 && <Chip size="small" label={`+${numSelected}`} />}
                                            </Box>
                                        );
                                    }}
                                >
                                    {userRoles.map((role) => {
                                        return (
                                            <MenuItem
                                                key={role.id}
                                                value={role.id}
                                                className={classes.roleSelectMenuItem}
                                            >
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox checked={filters.filter_roles.includes(role.id)} />
                                                    <ListItemText primary={role.label} />
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                    <li className={classes.selectMenuClear}>
                                        <ClearFilterButton
                                            isDisabled={filters.filter_roles.includes("placeholder")}
                                            onClick={() => handleClearFilters("filter_roles")}
                                        />
                                    </li>
                                </Select>
                            </Box>
                            <Box sx={{ maxWidth: 200 }}>
                                <FormLabel className={classes.label}>
                                    {intl.formatMessage({ id: "filters.labels.team" })}
                                </FormLabel>
                                <Select
                                    multiple
                                    id="filter_teams"
                                    name="filter_teams"
                                    labelId="filter-by-team"
                                    value={filters.filter_teams}
                                    className={classes.selectField}
                                    onChange={handleFilter}
                                    IconComponent={(props) => (
                                        <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                                    )}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                    renderValue={(selected) => {
                                        const numSelected = selected.length - 1;
                                        const lastSelected = teamsData.find(
                                            (team) => team.id === selected.at(-1) || team.name === selected.at(-1)
                                        );

                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography
                                                    className={cn({
                                                        [classes.filterPlaceholderLabel]:
                                                            lastSelected?.id === "placeholder",
                                                    })}
                                                >
                                                    {lastSelected?.name}
                                                </Typography>
                                                {numSelected > 0 && <Chip size="small" label={`+${numSelected}`} />}
                                            </Box>
                                        );
                                    }}
                                >
                                    {/* IMPORTANT: Teams API response do not have an id. Using generateID() to set ids after request!!! */}
                                    {teamsData.map((team) => {
                                        return (
                                            <MenuItem
                                                key={team.id}
                                                value={team.id === "placeholder" ? team.id : team.name}
                                                className={classes.roleSelectMenuItem}
                                            >
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={
                                                            filters.filter_teams.includes(team.name) ||
                                                            filters.filter_teams.includes(team.id as string)
                                                        }
                                                    />
                                                    {team.id === "placeholder" && <ListItemText primary={team.name} />}
                                                    {team.name === "no-team" && (
                                                        <Chip variant="outlined" label={team.name} />
                                                    )}
                                                    {team.id !== "placeholder" && team.name !== "no-team" && (
                                                        <Chip color="default" label={team.name} />
                                                    )}
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                    <li className={classes.selectMenuClear}>
                                        <ClearFilterButton
                                            isDisabled={filters.filter_teams.includes("placeholder")}
                                            onClick={() => handleClearFilters("filter_teams")}
                                        />
                                    </li>
                                </Select>
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <NavButton
                                disabled={!isAnyUserSelected}
                                variant="outlined"
                                showBackArrow={false}
                                onClick={handleOpenTeamsModal}
                                startIcon={<CustomIcon icon={icons.check} />}
                                sx={{ mr: 1 }}
                            >
                                Add to Team
                            </NavButton>
                            {canInviteUsers && (
                                <NavButton
                                    showBackArrow={false}
                                    onClick={handleInviteSelectedUsers}
                                    startIcon={<CustomIcon icon={icons.plus} />}
                                >
                                    Invite New User
                                </NavButton>
                            )}
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <ClearFilterButton
                            isDisabled={disableClearAll}
                            onClick={() => handleClearFilters("placeholder")}
                        />
                        {canInviteUsers && (
                            <Box display="flex" alignItems="center">
                                {pendingInvitations.length > 0 && (
                                    <Button
                                        onClick={handleOpenInvitationModal}
                                        className={classes.invitationsButton}
                                        variant="text"
                                    >
                                        {intl.formatMessage({
                                            id: "hubManagement.client.managePendingInvitations",
                                        })}{" "}
                                        ({pendingInvitations.length})
                                    </Button>
                                )}

                                <ArchivedUsers
                                    users={archivedUsers}
                                    userRoles={userRoles}
                                    onUnarchiveUser={handleUnarchiveUser}
                                    onUpdateRole={handleSelectRole}
                                    adminsCount={adminsCount}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Users */}
                <Box className={classes.usersTable}>
                    <TablePagination
                        currentPage={users.meta.page_no}
                        totalPages={users.meta.pages}
                        pageValue={currentPage}
                        onPageChange={handlePageChange}
                        handleInputChange={handleInputChange}
                        handleOnBlur={handleOnBlur}
                        handleOnKeyDown={handleOnKeyDown}
                        inputRef={paginationTextInputRef}
                    />
                    <TableContainer>
                        <Table>
                            <TableHead className={classes.tableHeader}>
                                <TableRow>
                                    <TableCell sx={{ width: 24 }}>
                                        <Checkbox
                                            size="small"
                                            disabled={disableAll}
                                            onChange={toggleCheckAllUsers}
                                            inputProps={{ "aria-label": "Select all users" }}
                                        />
                                    </TableCell>
                                    {tableHeader
                                        .filter((cell) => !cell.isHidden)
                                        .map((cell) => {
                                            return (
                                                <TableCell key={cell.id} sx={cell.sx}>
                                                    {cell.isSortable ? (
                                                        <Box className={classes.cellWithSortButton}>
                                                            <SortMenu
                                                                columnId={cell.id}
                                                                currentSortBy={usersSort}
                                                                sortConfig={cell.sort}
                                                                disabled={disableAll}
                                                                icon={
                                                                    <CustomIcon
                                                                        className={classes.sortButton}
                                                                        icon={icons.sortArrows}
                                                                    />
                                                                }
                                                                {...(!isEmpty(cell.sort)
                                                                    ? {
                                                                          onSort: (order, columnId) =>
                                                                              handleSort(order, columnId),
                                                                      }
                                                                    : {})}
                                                            />
                                                            <Typography>
                                                                {cell.name}{" "}
                                                                {cell.id === "name" ? `(${users.meta.hits})` : ""}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography>{cell.name}</Typography>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    <TableCell sx={{ width: 24 }} align="center">
                                        <HideColumnsMenu
                                            disabled={disableAll}
                                            items={hidableColumns}
                                            onClickItem={handleToggleColumn}
                                            icon={<CustomIcon icon={icons.threeDots} sx={{ fontSize: 24 }} />}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            {users.status === "fetching" && (
                                <TableBody className={classes.tableBody}>
                                    {Array.from(Array(6)).map((el, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={hidableColumns.length + 4}>
                                                <Skeleton variant="rounded" animation="wave" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                            {users.status === "done" && Boolean(users.data.length) && (
                                <TableBody className={classes.tableBody}>
                                    {users.data.map((user) => (
                                        <UserTableRow
                                            key={user.id}
                                            data={user}
                                            teams={teamsData}
                                            Checkbox={
                                                <Checkbox
                                                    size="small"
                                                    checked={user.checked}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(ev) => toggleCheckUser(ev, user.id)}
                                                    inputProps={{
                                                        "aria-label": "Select user",
                                                    }}
                                                />
                                            }
                                            RoleSelectInput={
                                                <UserRoleSelect
                                                    data={user}
                                                    onRoleSelection={handleSelectRole}
                                                    adminsCount={adminsCount}
                                                    list={userRoles}
                                                />
                                            }
                                            visibleColumns={visibleColumns}
                                            hidableColumns={hidableColumns}
                                            handleOpenArchiveModal={handleOpenArchiveModal}
                                            onChipSelect={handleChipSelect}
                                            setSelectedUser={() => setSelectedUserForTeam(user.id)}
                                            handleConfirmAddTeam={handleConfirmAddTeam}
                                            removeTeam={handleRemoveTeamFromUser}
                                        />
                                    ))}
                                </TableBody>
                            )}

                            {users.status === "done" && !Boolean(users.data.length) && (
                                <TableBody className={classes.tableBody}>
                                    <TableRow>
                                        <TableCell colSpan={hidableColumns.length + 3}>
                                            <Box className={classes.noDataWrapper}>
                                                <img
                                                    width={400}
                                                    src={drax_lighthouse_animation}
                                                    alt="Results Not Found"
                                                />
                                                <Typography>We couldn’t find any matches.</Typography>
                                                <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                                                    Please double check your search or try again.
                                                </Typography>
                                                <Box display="flex" alignItems="center">
                                                    <NavButton
                                                        variant="outlined"
                                                        showBackArrow={false}
                                                        onClick={() => handleClearFilters("placeholder")}
                                                        sx={{ mr: 1 }}
                                                        startIcon={<CustomIcon icon={icons.close} />}
                                                    >
                                                        Clear Search
                                                    </NavButton>
                                                    <NavButton
                                                        showBackArrow={false}
                                                        onClick={() => console.log("clicked")}
                                                        startIcon={<CustomIcon icon={icons.plus} />}
                                                    >
                                                        Invite New User
                                                    </NavButton>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </Box>

                <ArchiveUserConfirmation
                    open={shouldOpenArchiveModal}
                    closeConfirmation={() => handleCloseArchiveModal("cancel")}
                    handleArchiveUser={() => handleArchiveUser()}
                    userName={selectedUserName}
                />

                <Modal open={shouldOpenTeamsModal}>
                    <SelectTeam
                        list={teamsData}
                        users={users.data}
                        cancel={() => handleCloseTeamsModal("cancel")}
                        confirm={(selectedUserIds, newTeams) => handleConfirmAddTeam(selectedUserIds, newTeams)}
                        isMultipleUsers
                    />
                </Modal>

                <InviteUsersModal
                    open={inviteModalOpen}
                    activeTab={activeTab}
                    onClose={closeInviteModal}
                    pendingInvitations={pendingInvitations}
                    org_id={orgId}
                />
            </Body>
        </>
    );
};

export default UserManagement;
